import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ImageSize = '512x512' | '768x1024' | '1024x768';

export interface ImageGenerationSettings {
  selectedModelId: string;
  imageSize: ImageSize;
  steps: number;
  quantity: number;
  seed: number | 'random';
}

interface ImageSettingsState extends ImageGenerationSettings {
  setSelectedModelId: (modelId: string) => void;
  setImageSize: (size: ImageSize) => void;
  setSteps: (steps: number) => void;
  setQuantity: (quantity: number) => void;
  setSeed: (seed: number | 'random') => void;
  resetToDefaults: () => void;
}

const defaultSettings: ImageGenerationSettings = {
  selectedModelId: '',
  imageSize: '512x512',
  steps: 25,
  quantity: 1,
  seed: 'random',
};

const validateSteps = (steps: number): number => {
  return Math.max(1, Math.min(50, Math.round(steps)));
};

const validateQuantity = (quantity: number): number => {
  return Math.max(1, Math.min(4, Math.round(quantity)));
};

const validateSeed = (seed: number | 'random'): number | 'random' => {
  if (seed === 'random') return 'random';
  const numSeed = Number(seed);
  return isNaN(numSeed) ? 'random' : Math.max(0, Math.round(numSeed));
};

export const useImageSettingsStore = create<ImageSettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,
      setSelectedModelId: (selectedModelId: string) => 
        set({ selectedModelId }),
      setImageSize: (imageSize: ImageSize) => 
        set({ imageSize }),
      setSteps: (steps: number) => 
        set({ steps: validateSteps(steps) }),
      setQuantity: (quantity: number) => 
        set({ quantity: validateQuantity(quantity) }),
      setSeed: (seed: number | 'random') => 
        set({ seed: validateSeed(seed) }),
      resetToDefaults: () => 
        set(defaultSettings),
    }),
    {
      name: 'image-settings-storage',
      partialize: (state) => ({
        selectedModelId: state.selectedModelId,
        imageSize: state.imageSize,
        steps: state.steps,
        quantity: state.quantity,
        seed: state.seed,
      }),
    }
  )
);

// Selector hooks for optimized component consumption
export const useSelectedModelId = () => useImageSettingsStore(state => state.selectedModelId);
export const useImageSize = () => useImageSettingsStore(state => state.imageSize);
export const useSteps = () => useImageSettingsStore(state => state.steps);
export const useQuantity = () => useImageSettingsStore(state => state.quantity);
export const useSeed = () => useImageSettingsStore(state => state.seed);

// Action hooks
export const useImageSettingsActions = () => useImageSettingsStore(state => ({
  setSelectedModelId: state.setSelectedModelId,
  setImageSize: state.setImageSize,
  setSteps: state.setSteps,
  setQuantity: state.setQuantity,
  setSeed: state.setSeed,
  resetToDefaults: state.resetToDefaults,
}));